<script lang="ts" setup>
import { ref, computed, Ref, onBeforeMount, onBeforeUnmount, inject, getCurrentInstance } from 'vue';
import { useEvents } from '@/plugins/events';
import { useLocalization } from '@/plugins/localization';
import { usePermissions } from '@/plugins/permissions';
import { KeyValuePair } from '@/helpers/Interfaces';
import { FormEntry } from '../form';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { ContractorEntry, ContractorType, instanceOfContractorEntry } from '.';
import properties from '../../properties';
import { ContractorRole, ContractorRoleOptions } from '@/modules/logito/crm/inc/Enums';
import { computedAsync } from '@vueuse/core';
import Permissions from '@/settings/permissions';
import FormContractorModal from './modals/FormContractorModal.vue';
import { useRoute } from 'vue-router';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useDynamicTrigger } from '@/components/forms/helpers/DynamicTriggerHelper';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';
import { registerComponent, unRegisterComponent } from '@/store/triggers';
import { createTriggerComponentCtx } from '@/components/builder/form/traits/TriggerContext';

defineOptions({
    name: 'contractor-blueprint',
    components: {
        ...properties,
    },
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const { $permissions } = usePermissions();
const { $t } = useLocalization();
const route = useRoute();
const { $events } = useEvents();
const level = inject<number>('level', 0);

const entryData = ref(new ContractorEntry()) as Ref<ContractorEntry>;
const formContractorModalRef = ref<InstanceType<typeof FormContractorModal> | null>(null);
const reloadKey = ref(0);
const partnerAddedPublicId = ref('');
const isExternalForm = computed(() => route.meta.externalAccess === true);

const triggerComponentCtx = createTriggerComponentCtx(
    {

    }
);

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instanceOfContractorEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);

        registerComponent(props.form.blueprint.id, props.blueprint.name, (getCurrentInstance()?.props), triggerComponentCtx);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();

        unRegisterComponent(props.form.blueprint.id, props.blueprint.name);
    }
});

const blueprint = computed(() => props.blueprint);

const value = computed({
    get()
    {
        const { data: value } = entryData.value;

        if (!blueprint.value.multiselect) return value?.[0] ?? null;

        return value ?? null;
    },
    set(value: KeyValuePair[] | KeyValuePair)
    {
        entryData.value.data = Array.isArray(value) ? value : value ? [value] : [];
        useDynamicTrigger($events, props.form.blueprint.id).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? props.index : null);
    },
});

const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
const placeholder = computed(() => props.form.localization.translate(props.blueprint.placeholder));
const design = computed(() => props.form.designMode());
const endpoint = computed(() =>
{
    if (design.value) return null;

    if (isExternalForm.value)
    {
        return `external-form/${props.entry.module}/${props.entry.formId}/select-options/${props.entry.actionName}/${blueprint.value.name}`;
    }

    if (props.entry.fieldIndex)
    {
        return `modules/${props.entry.moduleId}/table-templates/select-options/${props.entry.fieldIndex}/${blueprint.value.name}`;
    }

    return `simple-dictionary/${props.entry.module}/form/${props.entry.formId}/${props.entry.actionName}/${blueprint.value.name}`;
});
const roleOptions = computed(() => ContractorRoleOptions().map(p =>
{
    return {
        value: ContractorRole[p.value],
        text: p.text
    };
}));

const isButton = computedAsync(async () => isExternalForm.value ? false : await $permissions.all([Permissions.Logito.Crm.CanEdit]));
const editMode = computed(() => isButton.value && !blueprint.value.multiselect && value.value);
const iconButton = computed(() => !editMode.value ? 'fa fa-plus' : 'fa fa-edit');
const titleButton = computed(() => !editMode.value ? $t('[[[Dodaj kontrahenta]]]') : $t('[[[Edytuj kontrahenta]]]'));
const publicId = computed(() => !Array.isArray(value.value) ? value.value?.key : '' );

const openModal = () =>
{
    formContractorModalRef.value.showModal();
};

const partnerAdded = (publicId: string) =>
{
    partnerAddedPublicId.value = publicId;
    reloadKey.value++;
};
</script>

<template>
    <form-component-wrapper class="register-component" :form="form" :parent="parent" :blueprint="blueprint" :table-row-index="props.index">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <div class="d-flex">
                    <ideo-select
                        v-model="value"
                        :placeholder="placeholder"
                        :multiselect="blueprint.multiselect"
                        :endpoint="endpoint"
                        :deselect="true"
                        :clear-button="!isButton"
                        class="flex-fill"
                        :reload-key="reloadKey"
                        :reload-value="partnerAddedPublicId"
                        :custom-classes="{ 'border-radius-right-none': isButton }"
                    >
                        <template #option="{ option }">
                            <div class="d-flex flex-column">
                                <strong>{{ option.value }}</strong>
                                <small v-if="option.nip">{{ $t('[[[NIP]]]') }}: {{ option.nip }}</small>
                                <small>{{ $t('[[[Adres]]]') }}: {{ option.adress }}</small>
                                <small v-if="option.companies">{{ $t('[[[Spółki]]]') }}: {{ option.companies }}</small>
                                <small v-if="option.source"> {{ $t('[[[Źródło]]]') }}: {{ option.source.value }}</small>
                            </div>
                        </template>
                    </ideo-select>
                    <ideo-button
                        v-if="isButton"
                        variant="success"
                        type="button"
                        class="border-radius-left-none"
                        :title="titleButton"
                        @click="openModal"
                    >
                        <i :class="iconButton"></i>
                    </ideo-button>
                </div>
                <form-error-message :entry="entryData" name="value" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
            <form-contractor-modal ref="formContractorModalRef" :public-id="publicId" @partner-added="partnerAdded" />
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3 docs-container">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <field-checkbox
                        v-model="blueprint.multiselect"
                        :label="$t('[[[Wielokrotny wybór]]]')"
                        :invalid-feedback="() => form.schema.errorMessage(blueprint, 'multiselect')"
                    />
                    <field-checkbox v-model="blueprint.filterByRole" :label="$t('[[[Filtruj po roli]]]')" />
                    <div class="mb-2 ps-2" v-if="blueprint.filterByRole">
                        <ideo-form-radio-group v-model="blueprint.roleFilter" :options="roleOptions" />
                    </div>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.placeholder[locale]" :label="$t('[[[Tekst zastępczy]]]')" />
                    </ideo-form-localize>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-visible :form="form" :blueprint="blueprint" :label="$t('[[[Widoczność]]]')" />
                    <field-required :form="form" :blueprint="blueprint" :label="$t('[[[Pole wymagane]]]')" />
                    <field-error :form="form" :blueprint="blueprint" :label="$t('[[[Niestandardowy błąd]]]')" />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>
